
import type { Node, Edge, ReactFlowInstance } from '@xyflow/react'
import {
  IMAGE_DROPDOWN_ITEMS,
  VIDEO_DROPDOWN_ITEMS,
  TEXT_DROPDOWN_ITEMS,
  type DropdownItem,
} from '@/components/canvas/toolbar/toolbar-config'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface CommandContext {
  selectedNodes: Node[]
  selectedEdges: Edge[]
  nodes: Node[]
  edges: Edge[]
  canvasRef: React.RefObject<ReactFlowInstance | null>
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  actions: {
    undo: () => void
    redo: () => void
    deleteSelected: () => void
    selectAll: () => void
    deselectAll: () => void
    duplicateSelected: () => void
    copySelected: () => void
    paste: () => void
    cut: () => void
    toggleLock: () => void
    centerOnSelected: () => void
    changeZIndex: (delta: number) => void
  }
  callbacks: {
    onToggleShortcuts?: () => void
    onToggleProperties?: () => void
    onToggleSidebar?: () => void
    onToggleAllSidebars?: () => void
    onAddNode?: (type: 'text' | 'image' | 'video' | 'sound' | 'shader') => void
    onToolSelect?: (toolId: string) => void
    onOpenCommandPalette?: () => void
  }
}

export interface CanvasCommand {
  id: string
  label: string
  group: string
  keywords?: string[]
  shortcut?: string
  icon?: string
  when?: (ctx: CommandContext) => boolean
  execute: (ctx: CommandContext) => void
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const hasSelection = (ctx: CommandContext) => ctx.selectedNodes.length > 0
const hasNodes = (ctx: CommandContext) => ctx.nodes.length > 0

/* ------------------------------------------------------------------ */
/*  Node commands (from toolbar dropdown items)                        */
/* ------------------------------------------------------------------ */

/** Base quick-add key per node category */
const CATEGORY_KEY: Record<string, string> = {
  text: 'T',
  image: 'I',
  video: 'V',
}

/** Build two-step shortcut: "T" for default, "T → 2" for variants */
function nodeShortcut(category: string, variantIndex?: string): string | undefined {
  const base = CATEGORY_KEY[category]
  if (!base) return undefined
  if (!variantIndex || variantIndex === '1') return base
  return `${base} → ${variantIndex}`
}

function buildNodeCommands(): CanvasCommand[] {
  const commands: CanvasCommand[] = []

  for (const item of TEXT_DROPDOWN_ITEMS) {
    if (item.children) {
      // Flatten nested tools into individual commands
      for (const child of item.children) {
        commands.push({
          id: `node.${child.id}`,
          label: child.label,
          group: 'Text',
          icon: child.icon,
          keywords: ['text', child.label.toLowerCase()],
          execute: (ctx) => ctx.callbacks.onToolSelect?.(child.id),
        })
      }
      continue
    }
    commands.push({
      id: `node.${item.id}`,
      label: item.label,
      group: 'Text',
      icon: item.icon,
      shortcut: nodeShortcut('text', item.shortcut),
      execute: (ctx) => ctx.callbacks.onToolSelect?.(item.id),
    })
  }

  for (const item of IMAGE_DROPDOWN_ITEMS) {
    if (item.children) continue
    commands.push({
      id: `node.${item.id}`,
      label: item.label,
      group: 'Image',
      icon: item.icon,
      shortcut: nodeShortcut('image', item.shortcut),
      keywords: ['image', item.label.toLowerCase()],
      execute: (ctx) => ctx.callbacks.onToolSelect?.(item.id),
    })
  }

  for (const item of VIDEO_DROPDOWN_ITEMS) {
    if (item.children) continue
    commands.push({
      id: `node.${item.id}`,
      label: item.label,
      group: 'Video',
      icon: item.icon,
      shortcut: nodeShortcut('video', item.shortcut),
      keywords: ['video', item.label.toLowerCase()],
      execute: (ctx) => ctx.callbacks.onToolSelect?.(item.id),
    })
  }

  // Library
  commands.push({
    id: 'node.library',
    label: 'Browse Library',
    group: 'Library',
    icon: 'library',
    shortcut: 'L',
    keywords: ['library', 'models', 'browse'],
    execute: () => document.dispatchEvent(new CustomEvent('genie:open-library')),
  })

  return commands
}

/* ------------------------------------------------------------------ */
/*  Tool commands (nested: category → sub-purpose)                     */
/* ------------------------------------------------------------------ */

function flattenToolItems(items: DropdownItem[], prefix: string): { commands: CanvasCommand[]; groupNames: string[] } {
  const commands: CanvasCommand[] = []
  const groupNames: string[] = []

  for (const item of items) {
    if (!item.children) continue

    for (const category of item.children ?? []) {
      const groupName = `${prefix} · ${category.label}`
      groupNames.push(groupName)
      for (const subTool of category.children ?? []) {
        commands.push({
          id: `tool.${subTool.id}`,
          label: subTool.label,
          group: groupName,
          icon: subTool.icon,
          keywords: [prefix.toLowerCase(), 'tools', category.label.toLowerCase(), subTool.label.toLowerCase()],
          execute: (ctx) => ctx.callbacks.onToolSelect?.(subTool.id),
        })
      }
    }
  }

  return { commands, groupNames }
}

const imageTools = flattenToolItems(IMAGE_DROPDOWN_ITEMS, 'Image')
const videoTools = flattenToolItems(VIDEO_DROPDOWN_ITEMS, 'Video')
const toolGroupNames = [...imageTools.groupNames, ...videoTools.groupNames]

function buildToolCommands(): CanvasCommand[] {
  return [...imageTools.commands, ...videoTools.commands]
}

/* ------------------------------------------------------------------ */
/*  Canvas commands (edit, view, navigation, panels)                   */
/* ------------------------------------------------------------------ */

function buildCanvasCommands(): CanvasCommand[] {
  return [
    // ── Edit ──
    {
      id: 'edit.delete',
      label: 'Delete selected',
      group: 'Edit',
      shortcut: '⌫',
      icon: 'trash-2',
      when: hasSelection,
      execute: (ctx) => ctx.actions.deleteSelected(),
    },
    {
      id: 'edit.duplicate',
      label: 'Duplicate selected',
      group: 'Edit',
      shortcut: '⌘D',
      icon: 'copy',
      when: hasSelection,
      execute: (ctx) => ctx.actions.duplicateSelected(),
    },
    {
      id: 'edit.copy',
      label: 'Copy selected',
      group: 'Edit',
      shortcut: '⌘C',
      icon: 'clipboard-copy',
      when: hasSelection,
      execute: (ctx) => ctx.actions.copySelected(),
    },
    {
      id: 'edit.paste',
      label: 'Paste',
      group: 'Edit',
      shortcut: '⌘V',
      icon: 'clipboard-paste',
      execute: (ctx) => ctx.actions.paste(),
    },
    {
      id: 'edit.cut',
      label: 'Cut selected',
      group: 'Edit',
      shortcut: '⌘X',
      icon: 'scissors',
      when: hasSelection,
      execute: (ctx) => ctx.actions.cut(),
    },
    {
      id: 'edit.select-all',
      label: 'Select all nodes',
      group: 'Edit',
      shortcut: '⌘A',
      icon: 'check-square',
      when: hasNodes,
      execute: (ctx) => ctx.actions.selectAll(),
    },
    {
      id: 'edit.deselect',
      label: 'Deselect all',
      group: 'Edit',
      shortcut: 'Esc',
      icon: 'x',
      when: hasSelection,
      execute: (ctx) => ctx.actions.deselectAll(),
    },
    {
      id: 'edit.undo',
      label: 'Undo',
      group: 'Edit',
      shortcut: '⌘Z',
      icon: 'undo-2',
      execute: (ctx) => ctx.actions.undo(),
    },
    {
      id: 'edit.redo',
      label: 'Redo',
      group: 'Edit',
      shortcut: '⌘⇧Z',
      icon: 'redo-2',
      execute: (ctx) => ctx.actions.redo(),
    },
    {
      id: 'edit.lock',
      label: 'Lock / Unlock selected',
      group: 'Edit',
      shortcut: '⌘L',
      icon: 'lock',
      when: hasSelection,
      execute: (ctx) => ctx.actions.toggleLock(),
    },
    {
      id: 'edit.bring-forward',
      label: 'Bring forward',
      group: 'Edit',
      shortcut: '⌘]',
      icon: 'arrow-up',
      when: hasSelection,
      execute: (ctx) => ctx.actions.changeZIndex(1),
    },
    {
      id: 'edit.send-backward',
      label: 'Send backward',
      group: 'Edit',
      shortcut: '⌘[',
      icon: 'arrow-down',
      when: hasSelection,
      execute: (ctx) => ctx.actions.changeZIndex(-1),
    },
    {
      id: 'edit.rename',
      label: 'Rename selected node',
      group: 'Edit',
      shortcut: 'F2',
      icon: 'pencil',
      when: (ctx) => ctx.selectedNodes.length === 1,
      execute: () => document.dispatchEvent(new CustomEvent('genie:rename-selected')),
    },
    {
      id: 'edit.generate',
      label: 'Generate selected node',
      group: 'Edit',
      shortcut: '⌘↵',
      icon: 'sparkles',
      when: (ctx) => ctx.selectedNodes.length === 1,
      execute: () => document.dispatchEvent(new CustomEvent('genie:generate-selected')),
    },

    // ── View ──
    {
      id: 'view.zoom-in',
      label: 'Zoom in',
      group: 'View',
      shortcut: '⌘+',
      icon: 'zoom-in',
      execute: (ctx) => ctx.canvasRef.current?.zoomIn(),
    },
    {
      id: 'view.zoom-out',
      label: 'Zoom out',
      group: 'View',
      shortcut: '⌘−',
      icon: 'zoom-out',
      execute: (ctx) => ctx.canvasRef.current?.zoomOut(),
    },
    {
      id: 'view.fit-view',
      label: 'Zoom to fit all',
      group: 'View',
      shortcut: '⌘0',
      icon: 'maximize-2',
      execute: (ctx) => ctx.canvasRef.current?.fitView({ duration: 300 }),
    },
    {
      id: 'view.zoom-100',
      label: 'Zoom to 100%',
      group: 'View',
      shortcut: '⇧0',
      icon: 'scan',
      execute: (ctx) => ctx.canvasRef.current?.zoomTo(1),
    },
    {
      id: 'view.shortcuts',
      label: 'Keyboard shortcuts',
      group: 'View',
      shortcut: '⌘/',
      icon: 'keyboard',
      execute: (ctx) => ctx.callbacks.onToggleShortcuts?.(),
    },

    // ── Navigation ──
    {
      id: 'nav.center-selected',
      label: 'Center on selected',
      group: 'View',
      shortcut: '⌘E',
      icon: 'crosshair',
      when: hasSelection,
      execute: (ctx) => ctx.actions.centerOnSelected(),
    },

    // ── Panels ──
    {
      id: 'canvas.toggle-properties',
      label: 'Toggle properties panel',
      group: 'View',
      shortcut: 'P',
      icon: 'panel-right',
      execute: (ctx) => ctx.callbacks.onToggleProperties?.(),
    },
    {
      id: 'canvas.toggle-sidebar',
      label: 'Toggle sidebar',
      group: 'View',
      shortcut: '\\',
      icon: 'panel-left',
      execute: (ctx) => ctx.callbacks.onToggleSidebar?.(),
    },
    {
      id: 'canvas.toggle-all-sidebars',
      label: 'Toggle all sidebars',
      group: 'View',
      shortcut: '⌘\\',
      icon: 'columns-2',
      execute: (ctx) => ctx.callbacks.onToggleAllSidebars?.(),
    },
  ]
}

/* ------------------------------------------------------------------ */
/*  Full registry (built once at module load)                          */
/* ------------------------------------------------------------------ */

export const CANVAS_COMMANDS: CanvasCommand[] = [
  ...buildNodeCommands(),
  ...buildToolCommands(),
  ...buildCanvasCommands(),
]

/* ------------------------------------------------------------------ */
/*  Group order: Nodes first, then Tools, then Canvas                  */
/* ------------------------------------------------------------------ */

const GROUP_ORDER = [
  // Nodes
  'Text', 'Image', 'Video', 'Library',
  // Tools (sub-categories from toolbar config)
  ...toolGroupNames,
  // Canvas
  'Edit', 'View',
]

export type TabId = 'all' | 'nodes' | 'tools' | 'canvas'

const GROUP_TO_TAB: Record<string, TabId> = {
  'Text': 'nodes',
  'Image': 'nodes',
  'Video': 'nodes',
  'Library': 'nodes',
  ...Object.fromEntries(toolGroupNames.map(name => [name, 'tools' as TabId])),
  'Edit': 'canvas',
  'View': 'canvas',
}

export function getGroupedCommands(ctx: CommandContext, tab: TabId = 'all'): Map<string, CanvasCommand[]> {
  const groups = new Map<string, CanvasCommand[]>()

  for (const group of GROUP_ORDER) {
    if (tab !== 'all' && GROUP_TO_TAB[group] !== tab) continue
    groups.set(group, [])
  }

  for (const cmd of CANVAS_COMMANDS) {
    if (tab !== 'all' && GROUP_TO_TAB[cmd.group] !== tab) continue
    if (cmd.when && !cmd.when(ctx)) continue
    const list = groups.get(cmd.group)
    if (list) list.push(cmd)
  }

  // Remove empty groups
  for (const [key, value] of groups) {
    if (value.length === 0) groups.delete(key)
  }

  return groups
}
